import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { FCRAFooterText } from '@/components/layout/FCRADisclaimer';

export const TermsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'CollectABILITY Terms of Use | Authorized Service Provider';

    // Update meta description
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMetaTag('description', 'Review CollectABILITY\'s Terms governing lawful, non-data-broker use of our services. Includes arbitration, limitations of liability & FCRA disclaimer.');
  }, []);

  return (
    <div className="min-h-screen">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">CollectABILITY Website Terms of Use</h1>
        <p className="text-slate-600 mb-12">Last Updated: October 13, 2025</p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-700 mb-4">
              These Terms of Use ("Terms") govern your access to and use of websites, web apps, and related services operated by or on behalf of CollectABILITY, LLC ("CollectABILITY," "we," "us," or "our") including collectability.ai and any other pages that link to these Terms (collectively, the "Services"). By accessing or using the Services, you agree to these Terms. If you do not agree, do not use the Services.
            </p>
            <p className="text-slate-700 mb-4">
              You must be at least 18 years old and reside in the United States or its territories to use the Services. By using the Services, you represent that you meet these requirements and have authority to enter into these Terms (for yourself or any entity you represent).
            </p>
            <p className="text-slate-700 mb-4">
              <strong>Arbitration Notice:</strong> Section 15 contains a binding arbitration provision and class-action waiver.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Our Role: Service Provider / Processor (Not a Data Broker)</h2>
            <p className="text-slate-700 mb-4">
              CollectABILITY operates as a service provider (CPRA and similar state laws) and processor (GDPR/UK GDPR/PIPEDA context) to our business customers. We do not sell or share personal information for cross-context behavioral advertising and do not operate as a data broker. Any limited public-record information displayed through the Services is sourced from licensed third-party providers and handled solely to deliver the Services at a customer's instruction. See our{' '}
              <Link to="/privacy" className="text-blue-600 hover:text-slate-600">Privacy Policy</Link> for details.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. User Registration & Communications</h2>
            <p className="text-slate-700 mb-4">
              Some features require registration and accurate, current information. You are responsible for safeguarding your credentials and for actions taken under your account. You consent to receive service-related electronic communications (e.g., notices, transactional emails). You may opt out of marketing emails at any time via unsubscribe links.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. License to Use the Services</h2>
            <p className="text-slate-700 mb-4">
              Subject to these Terms, CollectABILITY grants you a limited, revocable, non-exclusive, non-transferable, non-sublicensable license to access and use the Services for your internal, lawful, non-commercial purposes (or, if you are a business customer, for your authorized business purposes under your agreement with CollectABILITY). All rights not expressly granted are reserved.
            </p>
            <p className="text-slate-700 mb-4">
              You shall not (and shall not enable others to): (a) copy, modify, or create derivative works of the Services or any content; (b) reverse engineer or attempt to extract source code (except to the extent such restriction is prohibited by law); (c) remove or alter proprietary notices; (d) use any scraping, harvesting, data mining, robots, or similar tools without our prior written consent; (e) circumvent security or access controls; (f) use the Services to infringe, violate, or misappropriate rights; (g) use the Services to stalk, harass, or otherwise harm any person; or (h) use the Services in a manner inconsistent with applicable law or these Terms.
            </p>
            <p className="text-slate-700 mb-4">
              We may modify, suspend, or discontinue any part of the Services at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Intellectual Property; Trademarks</h2>
            <p className="text-slate-700 mb-4">
              The Services and all content, designs, text, graphics, data, software, and other materials, including the CollectABILITY name and logos (collectively, "Content"), are owned by CollectABILITY or its licensors and protected by intellectual-property laws. Nothing grants you ownership of any Content. All CollectABILITY marks are our property and may not be used without our prior written permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. User Content & Feedback</h2>
            <p className="text-slate-700 mb-4">
              If you submit, upload, or transmit materials, data, or information to the Services ("User Content"), you are responsible for it and represent that you have all rights necessary to do so. You shall not submit unlawful, infringing, harmful, or otherwise objectionable content, nor content containing malware or designed to disrupt operations.
            </p>
            <p className="text-slate-700 mb-4">
              You grant CollectABILITY a non-exclusive, royalty-free, worldwide license to host, store, process, and display User Content solely to operate, secure, and improve the Services and fulfill your requests. Separately, if you provide Feedback (e.g., suggestions or ideas), CollectABILITY may use it without restriction and without compensation.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data-Use Restrictions (Very Important)</h2>
            <p className="text-slate-700 mb-4">
              You agree not to use the Services or any output to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-700">
              <li>engage in any unlawful discrimination or harassment;</li>
              <li>violate privacy, consumer-protection, or anti-spam laws;</li>
              <li>assemble, compile, resell, or republish personal data as a standalone dataset;</li>
              <li>conduct identity theft, doxxing, surveillance, or other harmful activities;</li>
              <li>create, furnish, or use "consumer reports" or "investigative consumer reports" for any purpose governed by the Fair Credit Reporting Act (FCRA), including decisions about employment, credit, insurance, housing, or similar eligibility.</li>
            </ul>
            <p className="text-slate-700 mb-4">
              <strong>FCRA Disclaimer:</strong> CollectABILITY is not a consumer reporting agency and the Services do not constitute "consumer reports." You must not use any information obtained through the Services for FCRA-regulated purposes (e.g., employment screening, credit eligibility, insurance underwriting, tenant screening, or any other eligibility determination).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Third-Party Sites & Services</h2>
            <p className="text-slate-700 mb-4">
              The Services may contain links to third-party websites or integrate third-party services. We do not control and are not responsible for third-party content, products, or services. Your use of third-party sites/services is at your own risk and subject to those providers' terms and policies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Privacy</h2>
            <p className="text-slate-700 mb-4">
              Your use of the Services is subject to our{' '}
              <Link to="/privacy" className="text-blue-600 hover:text-slate-600">Privacy Policy</Link>{' '}
              (posted on the site). Our Privacy Policy explains how we handle information and reinforces our service-provider/processor role and no-data-broker posture.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Security; Availability</h2>
            <p className="text-slate-700 mb-4">
              We use commercially reasonable safeguards to protect the Services, but no system is perfectly secure or available at all times. You are responsible for maintaining appropriate security and backups for your systems and data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. No Professional Advice</h2>
            <p className="text-slate-700 mb-4">
              The Services and any outputs or insights are provided for general informational purposes only. We do not provide legal, financial, credit, debt-collection, or other professional advice. You are responsible for obtaining professional advice as needed.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Indemnification</h2>
            <p className="text-slate-700 mb-4">
              You will indemnify, defend, and hold harmless CollectABILITY and its affiliates, officers, directors, employees, and agents from and against any claims, damages, liabilities, losses, and expenses (including reasonable attorneys' fees) arising out of or related to: (a) your use of the Services; (b) your User Content; (c) your violation of these Terms; or (d) your violation of law or third-party rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Disclaimers</h2>
            <p className="text-slate-700 mb-4 uppercase font-semibold">
              THE SERVICES AND ALL CONTENT ARE PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, COLLECTABILITY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT OF COURSE OF DEALING OR USAGE. WE DO NOT WARRANT THAT THE SERVICES WILL BE ERROR-FREE, SECURE, OR UNINTERRUPTED, OR THAT DEFECTS WILL BE CORRECTED.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Limitation of Liability</h2>
            <p className="text-slate-700 mb-4 uppercase font-semibold">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, COLLECTABILITY WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICES, EVEN IF ADVISED OF THE POSSIBILITY. OUR TOTAL LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICES WILL NOT EXCEED ONE HUNDRED DOLLARS (US $100). SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS; IN SUCH CASES, THE ABOVE LIMITATIONS APPLY TO THE FULLEST EXTENT PERMITTED.
            </p>
            <p className="text-slate-700 mb-4">
              If you are a California resident, you waive California Civil Code §1542.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Arbitration; Class-Action Waiver</h2>
            <p className="text-slate-700 mb-4">
              <strong>Binding Arbitration.</strong> Any dispute, claim, or controversy arising out of or relating to these Terms or the Services will be resolved by binding arbitration before a single arbitrator under the American Arbitration Association Consumer Arbitration Rules, unless you and we agree otherwise in writing. The Federal Arbitration Act governs interpretation and enforcement of this provision.
            </p>
            <p className="text-slate-700 mb-4">
              <strong>Venue; Fees.</strong> Unless otherwise agreed, any in-person hearing will occur in the U.S. county of your billing address. Your AAA filing fee will be limited to the amount set by AAA for consumer disputes.
            </p>
            <p className="text-slate-700 mb-4 uppercase font-semibold">
              <strong>Class-Action Waiver.</strong> YOU AND COLLECTABILITY AGREE THAT EACH MAY BRING CLAIMS ONLY IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY CLASS, REPRESENTATIVE, OR COLLECTIVE ACTION. THE ARBITRATOR MAY NOT CONSOLIDATE CLAIMS OF MORE THAN ONE PERSON OR OTHERWISE PRESIDE OVER ANY FORM OF A REPRESENTATIVE OR CLASS PROCEEDING.
            </p>
            <p className="text-slate-700 mb-4">
              If any part of this Section is found unenforceable, the entire arbitration agreement is void, and disputes will be resolved in court (see Section 17).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">16. Termination</h2>
            <p className="text-slate-700 mb-4">
              We may suspend or terminate your access to the Services at any time, with or without notice, for any reason. Upon termination, your right to use the Services ceases immediately. Sections that by their nature should survive (e.g., IP, Disclaimers, Limitation of Liability, Arbitration, Indemnification) will survive.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">17. Governing Law; Venue</h2>
            <p className="text-slate-700 mb-4">
              These Terms are governed by the laws of the State of Idaho, without regard to conflicts-of-law rules. Subject to Section 15 (Arbitration), the exclusive jurisdiction and venue for any action not subject to arbitration will be the state or federal courts located in Ada County, Idaho, and you consent to the personal jurisdiction of those courts.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">18. DMCA; Copyright</h2>
            <p className="text-slate-700 mb-4">
              If you believe content on the Services infringes your copyright, send a notice including the information required by 17 U.S.C. §512(c)(3) to our copyright agent:
            </p>
            <p className="text-slate-700 mb-4">
              CollectABILITY, LLC<br />
              Email: <a href="mailto:support@collectability.ai" className="text-blue-600 hover:text-slate-600">support@collectability.ai</a><br />
              Subject: DMCA Takedown Notice
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">19. Changes to These Terms</h2>
            <p className="text-slate-700 mb-4">
              We may update these Terms from time to time. The "Last Updated" date indicates the latest revision. Changes take effect when posted. If you continue using the Services after changes become effective, you accept the revised Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">20. Severability; Assignment; Waiver; Entire Agreement</h2>
            <p className="text-slate-700 mb-4">
              If any provision is held invalid, the remaining provisions will remain in effect. You may not assign these Terms without our prior written consent. Our failure to enforce any provision is not a waiver. These Terms constitute the entire agreement between you and CollectABILITY regarding the Services and supersede prior or contemporaneous agreements on the subject (excluding any separately executed MSA/DPA with CollectABILITY, which will control in case of conflict for your paid account).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">21. Contact</h2>
            <p className="text-slate-700 mb-4">
              Questions about these Terms:{' '}
              <a href="mailto:support@collectability.ai" className="text-blue-600 hover:text-slate-600">support@collectability.ai</a>
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-200 py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {/* Logo & About */}
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center mb-4" aria-label="CollectABILITY Home">
                <img
                  src="/linear-logo-sm.png"
                  alt="CollectABILITY"
                  className="h-10"
                  loading="lazy"
                  height="40"
                />
              </Link>
              <p className="text-slate-700 text-sm mb-4 max-w-md">
                AI-powered debt collection intelligence platform for smarter collections and higher returns.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="mailto:support@collectability.ai"
                  className="text-slate-700 hover:text-slate-900 text-sm transition-colors"
                >
                  support@collectability.ai
                </a>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="text-slate-900 font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/batch" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Batch Processing
                  </Link>
                </li>
                <li>
                  <Link to="/search" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Web Search
                  </Link>
                </li>
                <li>
                  <a href="/#features" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Features
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="text-slate-900 font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/#contact" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Contact Us
                  </a>
                </li>
                <li>
                  <Link to="/terms" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Terms of Use
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/account" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    My Account
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-300 pt-8 space-y-3">
            <FCRAFooterText />
            <p className="text-slate-700 text-sm text-center">
              &copy; 2024 CollectABILITY, LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};


